/**
 * Draft Entry Service
 * Handles auto-save functionality for production entries with enhanced sync tracking
 * and manager approval workflow for submitted entry edits
 */

import { indexedDBService } from '../offline/indexedDB';
import type { 
  DraftProductionEntry, 
  DraftFieldState, 
  ProductionEntry,
  FieldSyncStatus,
  EntrySubmissionStatus,
  EditApprovalRequest
} from '../../types/draftEntry';
import { getCompanyId } from '../tenantContext';

// Required fields for a complete entry
const REQUIRED_FIELDS = [
  'gasVolume',
  'casingPressure',
  'tubingPressure',
];

class DraftEntryService {
  /**
   * Create a new draft entry with enhanced status tracking
   */
  createNewDraft(
    wellId: string,
    wellName: string,
    userId: string,
    userName: string,
    entryDate: string
  ): DraftProductionEntry {
    const now = new Date().toISOString();
    const timeLabel = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return {
      id: crypto.randomUUID(),
      wellId,
      wellName,
      companyId: getCompanyId(),
      userId,
      userName,
      isDraft: true,
      isComplete: false,
      completionPercentage: 0,
      lastModifiedAt: now,
      createdAt: now,
      entryDate,
      timeLabel,
      fields: {},
      entryMethod: 'draft-autosave',
      syncStatus: 'pending',
      submissionStatus: 'draft',
    };
  }

  /**
   * Load draft entry for well + user + date
   * Returns existing draft or creates new one
   */
  async loadDraft(
    wellId: string,
    wellName: string,
    userId: string,
    userName: string,
    entryDate: string
  ): Promise<DraftProductionEntry> {

    // 1. Try IndexedDB first (instant)
    const localDraft = await indexedDBService.getDraftEntry(wellId, userId, entryDate);
    if (localDraft) {
      console.log('📝 Loaded existing draft from IndexedDB:', {
        draftId: localDraft.id,
        wellId: localDraft.wellId,
        userId: localDraft.userId,
        entryDate: localDraft.entryDate,
        fieldsCount: Object.keys(localDraft.fields || {}).length,
        hasTankReadings: !!localDraft.fields?.['tankReadings'],
        tankReadingsCount: Array.isArray(localDraft.fields?.['tankReadings']?.value) ? localDraft.fields?.['tankReadings']?.value.length : 0,
      });
      return localDraft;
    }


    // 2. Try Firestore (if online)
    if (navigator.onLine) {
      try {
        const remoteDraft = await this.fetchDraftFromFirestore(wellId, userId, entryDate);
        if (remoteDraft) {
          await indexedDBService.saveDraftEntry(remoteDraft);
          return remoteDraft;
        }
      } catch (error) {
        // Continue to create new draft
      }
    }

    // 3. Create new draft
    const newDraft = this.createNewDraft(wellId, wellName, userId, userName, entryDate);
    await indexedDBService.saveDraftEntry(newDraft);
    return newDraft;
  }

  /**
   * Fetch draft from Firestore (fallback when IndexedDB empty)
   */
  private async fetchDraftFromFirestore(
    wellId: string,
    userId: string,
    entryDate: string
  ): Promise<DraftProductionEntry | null> {
    try {
      const companyId = getCompanyId();

      // Import Firestore dynamically to avoid circular deps
      const { db } = await import('../firebase/client');
      const { collection, query, where, getDocs } = await import('firebase/firestore');

      // Query: companies/{companyId}/wells/{wellId}/draft_entries
      const draftsRef = collection(db, 'companies', companyId, 'wells', wellId, 'draft_entries');
      const q = query(
        draftsRef,
        where('userId', '==', userId),
        where('entryDate', '==', entryDate)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as DraftProductionEntry;
      }

      return null;
    } catch (error) {
      console.error('Firestore fetch error:', error);
      return null;
    }
  }

  /**
   * Save a field update with enhanced sync status tracking
   */
  async saveField(
    draftId: string,
    fieldName: string,
    value: any,
    onStatusChange?: (status: FieldSyncStatus) => void
  ): Promise<FieldSyncStatus> {
    const timestamp = new Date().toISOString();

    // First, verify the draft exists
    const existingDraft = await indexedDBService.getDraftEntryById(draftId);
    if (!existingDraft) {
      console.error(`❌ Cannot save field - draft ${draftId} not found in IndexedDB`);
      throw new Error(`Draft ${draftId} not found`);
    }

    // Entry locking removed - pumpers can now edit any entry
    // Previously: blocked edits to 'submitted' or 'confirmed' entries
    // if (existingDraft.submissionStatus === 'submitted' || 
    //     existingDraft.submissionStatus === 'confirmed') {
    //   throw new Error('Entry is submitted. Request manager approval to edit.');
    // }


    // Notify saving status
    onStatusChange?.('saving');

    // Validate field value
    const validation = this.validateField(fieldName, value);
    if (validation.hasError) {
      onStatusChange?.('error');
      throw new Error(validation.errorMessage || 'Invalid field value');
    }

    // Create field state with initial local status
    const fieldState: DraftFieldState = {
      value,
      savedAt: timestamp,
      isSaved: true,
      hasError: false,
      syncStatus: 'local',
      syncAttempts: 0,
    };

    // Save to IndexedDB immediately (offline-first)
    await indexedDBService.updateDraftField(draftId, fieldName, fieldState);
    onStatusChange?.('local');

    // Queue Firestore sync with retry logic
    if (navigator.onLine) {
      try {
        onStatusChange?.('syncing');
        await this.queueFieldSync(draftId, fieldName, fieldState);
        
        // Update to synced status after queuing
        fieldState.syncStatus = 'synced';
        fieldState.lastSyncedAt = new Date().toISOString();
        await indexedDBService.updateDraftField(draftId, fieldName, fieldState);
        onStatusChange?.('synced');
        
        return 'synced';
      } catch (error) {
        fieldState.syncStatus = 'local';
        fieldState.lastSyncError = error instanceof Error ? error.message : 'Sync failed';
        await indexedDBService.updateDraftField(draftId, fieldName, fieldState);
        onStatusChange?.('local');
        return 'local';
      }
    } else {
      // Still queue for later
      await this.queueFieldSync(draftId, fieldName, fieldState);
      return 'local';
    }
  }

  /**
   * Get sync status for all fields in a draft
   */
  async getFieldSyncStatuses(draftId: string): Promise<Map<string, FieldSyncStatus>> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    const statuses = new Map<string, FieldSyncStatus>();
    
    if (!draft) return statuses;
    
    for (const [fieldName, fieldState] of Object.entries(draft.fields || {})) {
      statuses.set(fieldName, fieldState.syncStatus || 'pristine');
    }
    
    return statuses;
  }

  /**
   * Count pending sync fields
   */
  async getPendingSyncCount(draftId: string): Promise<number> {
    const statuses = await this.getFieldSyncStatuses(draftId);
    let count = 0;
    
    for (const status of statuses.values()) {
      if (status === 'local' || status === 'syncing' || status === 'error') {
        count++;
      }
    }
    
    return count;
  }

  /**
   * Retry sync for a specific field
   */
  async retryFieldSync(
    draftId: string,
    fieldName: string,
    onStatusChange?: (status: FieldSyncStatus) => void
  ): Promise<FieldSyncStatus> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) throw new Error('Draft not found');
    
    const fieldState = draft.fields[fieldName];
    if (!fieldState) throw new Error('Field not found');
    
    fieldState.syncAttempts = (fieldState.syncAttempts || 0) + 1;
    
    return this.saveField(draftId, fieldName, fieldState.value, onStatusChange);
  }

  /**
   * Check if entry can be edited
   * Entry locking removed - pumpers can now edit any entry directly
   */
  async canEditEntry(draftId: string, userId: string): Promise<{ 
    canEdit: boolean; 
    reason?: string;
    requiresApproval?: boolean;
  }> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) {
      return { canEdit: false, reason: 'Entry not found' };
    }
    
    // All entries can now be edited - approval workflow removed
    return { canEdit: true };
  }

  /**
   * Request edit approval for a submitted entry
   * @deprecated Manager approval workflow has been removed
   */
  async requestEditApproval(
    draftId: string,
    userId: string,
    userName: string,
    reason: string,
    fieldsToEdit: string[]
  ): Promise<EditApprovalRequest> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) throw new Error('Entry not found');
    
    const request: EditApprovalRequest = {
      id: crypto.randomUUID(),
      entryId: draftId,
      wellId: draft.wellId,
      wellName: draft.wellName,
      requestedBy: { id: userId, name: userName },
      requestedAt: new Date().toISOString(),
      reason,
      fieldsToEdit,
      status: 'pending',
    };
    
    // Update draft status
    draft.submissionStatus = 'edit_requested';
    draft.editApprovalId = request.id;
    await indexedDBService.saveDraftEntry(draft);
    
    // Queue sync to server
    await indexedDBService.addToSyncQueue({
      type: 'CREATE',
      entity: 'edit_approval_request',
      data: {
        ...request,
        companyId: draft.companyId,
      },
      priority: 2, // High priority
    });
    
    return request;
  }

  /**
   * Process edit approval (for managers)
   */
  async processEditApproval(
    requestId: string,
    approved: boolean,
    reviewerId: string,
    reviewerName: string,
    notes?: string
  ): Promise<void> {
    // This would need to fetch the request and associated draft
    // Implementation depends on how requests are stored
    
    // Queue update to server
    await indexedDBService.addToSyncQueue({
      type: 'UPDATE',
      entity: 'edit_approval_request',
      data: {
        requestId,
        status: approved ? 'approved' : 'denied',
        reviewedBy: { id: reviewerId, name: reviewerName },
        reviewedAt: new Date().toISOString(),
        reviewNotes: notes,
      },
      priority: 2,
    });
  }

  /**
   * Submit entry (marks as officially submitted)
   */
  async submitEntry(draftId: string): Promise<void> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) throw new Error('Draft not found');
    
    // Validate required fields
    if (!this.isComplete(draft)) {
      throw new Error('Cannot submit incomplete entry. Missing required fields.');
    }
    
    // Update submission status
    draft.submissionStatus = 'submitted';
    draft.submittedAt = new Date().toISOString();
    draft.lastModifiedAt = new Date().toISOString();
    
    // Mark all fields as submitted
    for (const fieldName of Object.keys(draft.fields)) {
      if (draft.fields[fieldName]) {
        draft.fields[fieldName].syncStatus = 'submitted';
      }
    }
    
    await indexedDBService.saveDraftEntry(draft);
    
    // Queue sync to server
    await indexedDBService.addToSyncQueue({
      type: 'SUBMIT_ENTRY',
      entity: 'production_entry',
      data: {
        draftId,
        wellId: draft.wellId,
        companyId: draft.companyId,
        submittedAt: draft.submittedAt,
      },
      priority: 1, // Critical priority
    });
    
  }

  /**
   * Confirm entry submission (called when server confirms receipt)
   */
  async confirmSubmission(draftId: string): Promise<void> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) return;
    
    draft.submissionStatus = 'confirmed';
    draft.confirmedAt = new Date().toISOString();
    
    await indexedDBService.saveDraftEntry(draft);
  }

  /**
   * Queue field sync to Firestore (with retry support)
   */
  private async queueFieldSync(
    draftId: string,
    fieldName: string,
    fieldState: DraftFieldState
  ): Promise<void> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) return;

    await indexedDBService.addToSyncQueue({
      type: 'UPDATE_DRAFT_FIELD',
      entity: 'draft_entry',
      data: {
        draftId,
        wellId: draft.wellId,
        companyId: draft.companyId,
        fieldName,
        value: fieldState,
        userId: draft.userId, // Required for Firestore security rules
      },
      priority: 3, // Medium priority
    });
  }

  /**
   * Validate field value
   */
  validateField(fieldName: string, value: any): { hasError: boolean; errorMessage?: string } {
    // Empty values are allowed
    if (value === '' || value === null || value === undefined) {
      return { hasError: false };
    }

    // Number fields
    if (['gasVolume', 'casingPressure', 'tubingPressure', 'waterGauge', 'oilGauge', 'totalSales', 'maintenanceCost'].includes(fieldName)) {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;

      if (isNaN(numValue)) {
        return { hasError: true, errorMessage: 'Must be a valid number' };
      }

      if (numValue < 0) {
        return { hasError: true, errorMessage: 'Cannot be negative' };
      }

      // Pressure validations
      if (fieldName === 'casingPressure' && numValue > 5000) {
        return { hasError: true, errorMessage: 'Exceeds maximum 5000 PSI' };
      }

      if (fieldName === 'tubingPressure' && numValue > 3000) {
        return { hasError: true, errorMessage: 'Exceeds maximum 3000 PSI' };
      }
    }

    return { hasError: false };
  }

  /**
   * Calculate completion percentage
   */
  calculateCompletion(draft: DraftProductionEntry): number {
    const completed = REQUIRED_FIELDS.filter(field => {
      return draft.fields[field]?.isSaved === true;
    }).length;

    return Math.round((completed / REQUIRED_FIELDS.length) * 100);
  }

  /**
   * Check if draft is complete
   */
  isComplete(draft: DraftProductionEntry): boolean {
    return this.calculateCompletion(draft) === 100;
  }

  /**
   * Update draft completion status
   */
  async updateCompletion(draftId: string): Promise<void> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) return;

    const completionPercentage = this.calculateCompletion(draft);
    const isComplete = completionPercentage === 100;

    draft.completionPercentage = completionPercentage;
    draft.isComplete = isComplete;
    draft.lastModifiedAt = new Date().toISOString();

    await indexedDBService.saveDraftEntry(draft);
  }

  /**
   * Convert draft to final production entry
   */
  async finalizeDraft(draftId: string): Promise<ProductionEntry> {
    const draft = await indexedDBService.getDraftEntryById(draftId);
    if (!draft) throw new Error('Draft not found');

    // Validate required fields
    if (!this.isComplete(draft)) {
      throw new Error('Cannot finalize incomplete draft. Missing required fields.');
    }


    // Create final entry
    const entry: ProductionEntry = {
      id: crypto.randomUUID(),
      clientEntryId: crypto.randomUUID(),
      companyId: draft.companyId,
      wellId: draft.wellId,
      wellName: draft.wellName,
      entryDate: draft.entryDate,
      timestamp: new Date().toISOString(),
      recordedBy: {
        id: draft.userId,
        name: draft.userName,
      },
      gasVolumeMcf: draft.gasVolumeMcf,
      casingPressurePsi: draft.casingPressurePsi,
      tubingPressurePsi: draft.tubingPressurePsi,
      tankGauges: draft.tankGauges,
      gauges: draft.gauges,
      totalSalesBbl: draft.totalSalesBbl,
      maintenanceCost: draft.maintenanceCost,
      notes: draft.notes,
      equipmentStatus: draft.equipmentStatus,
      photos: draft.photos || [],
      voiceMemos: draft.voiceMemos || [],
      location: draft.location,
      entryMethod: 'draft-autosave',
      syncStatus: 'pending',
      deviceMetadata: draft.deviceMetadata,
    };

    // Save final entry to entries store
    await indexedDBService.saveEntries([entry]);

    // Queue for Firestore sync
    await indexedDBService.addToSyncQueue({
      type: 'CREATE',
      entity: 'entry',
      data: entry,
    });

    // Delete the draft
    await this.deleteDraft(draftId);


    return entry;
  }

  /**
   * Delete draft
   */
  async deleteDraft(draftId: string): Promise<void> {

    // Get metadata before deleting
    const draft = await indexedDBService.getDraftEntryById(draftId);

    await indexedDBService.deleteDraftEntry(draftId);

    if (draft) {
      // Queue Firestore deletion
      await indexedDBService.addToSyncQueue({
        type: 'DELETE',
        entity: 'draft_entry',
        data: {
          draftId,
          wellId: draft.wellId,
          companyId: draft.companyId
        },
      });
    }
  }

  /**
   * Get all drafts for current user
   */
  async getAllDrafts(userId: string): Promise<DraftProductionEntry[]> {
    const drafts = await indexedDBService.getAllDraftEntries(userId);

    // Sort by last modified (most recent first)
    return drafts.sort((a, b) =>
      new Date(b.lastModifiedAt).getTime() - new Date(a.lastModifiedAt).getTime()
    );
  }

  /**
   * Get drafts for a specific well
   */
  async getDraftsForWell(wellId: string): Promise<DraftProductionEntry[]> {
    return await indexedDBService.getDraftEntriesForWell(wellId);
  }

  /**
   * Auto-delete old drafts (> 7 days)
   */
  async pruneOldDrafts(maxAgeDays: number = 7): Promise<number> {
    const allDrafts = await indexedDBService.getAllDraftEntries('');
    const cutoffDate = Date.now() - (maxAgeDays * 24 * 60 * 60 * 1000);
    let deletedCount = 0;

    for (const draft of allDrafts) {
      const draftAge = new Date(draft.createdAt).getTime();
      if (draftAge < cutoffDate) {
        await this.deleteDraft(draft.id);
        deletedCount++;
      }
    }

    if (deletedCount > 0) {
    }

    return deletedCount;
  }
}

export const draftEntryService = new DraftEntryService();
